import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import {
  Ruler, UserCheck, Building2, FileCheck, Package, CalendarCheck,
  CheckCircle2, Circle, Clock, MessageCircle, ArrowRight, Search, Loader2,
} from 'lucide-react';
import { ordersApi } from '@/services/api';
import { toast } from 'sonner';

const STEP_ROUTES = ['/', '/schedule-visit', '/showroom', '/agreement', '/production', '/installation'];

const STEP_ICONS = [Ruler, UserCheck, Building2, FileCheck, Package, CalendarCheck];
const STEP_KEYS = ['measurements', 'techVisit', 'showroom', 'agreement', 'production', 'installation'];

const STATUS_CLASSES = {
  active: 'bg-gold/10 text-gold border border-gold/20',
  completed: 'bg-green-50 text-green-700 border border-green-200',
  cancelled: 'bg-red-50 text-red-600 border border-red-200',
};

const inputCls =
  'w-full px-4 py-3 border border-charcoal/15 bg-white/70 text-charcoal placeholder:text-charcoal/35 focus:outline-none focus:border-gold transition-colors';

const ClientDashboardPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [localData, setLocalData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lookupForm, setLookupForm] = useState({ orderNumber: '', password: '' });
  const [lookupLoading, setLookupLoading] = useState(false);
  const [lookupError, setLookupError] = useState(false);

  useEffect(() => {
    const stored = localStorage.getItem('arman_order');
    if (stored) {
      const local = JSON.parse(stored);
      setLocalData(local);
      ordersApi
        .getById(local.orderId)
        .then((res) => setOrder(res.data))
        .catch(() => {})
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  const handleLookup = async (e) => {
    e.preventDefault();
    if (!lookupForm.orderNumber || !lookupForm.password) return;
    setLookupLoading(true);
    setLookupError(false);
    try {
      const res = await ordersApi.lookupByPassword(lookupForm.orderNumber, lookupForm.password);
      const found = res.data;
      const local = {
        orderId: found.id,
        orderNumber: found.order_number,
        customerName: found.customer_name,
        customerPhone: found.customer_phone,
        accessPassword: found.access_password,
      };
      localStorage.setItem('arman_order', JSON.stringify(local));
      setLocalData(local);
      setOrder(found);
    } catch {
      setLookupError(true);
      toast.error(t('order.dashboard.notFound'));
    } finally {
      setLookupLoading(false);
    }
  };

  const currentStepRoute = order ? STEP_ROUTES[order.current_step - 1] || '/start' : '/start';
  const waLink = `https://wa.me/?text=${encodeURIComponent(
    `استفسار عن طلب مطبخي — ${localData?.orderNumber || ''}`
  )}`;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="animate-spin text-gold" size={36} />
      </div>
    );
  }

  return (
    <div className="min-h-screen py-24 md:py-32">
      <div className="container-custom max-w-3xl">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-10"
        >
          <span className="label-text mb-3 block">{t('order.journey')}</span>
          <h1 className="text-3xl md:text-4xl font-bold text-charcoal">{t('order.dashboard.title')}</h1>
          <p className="text-charcoal/55 mt-2">{t('order.dashboard.subtitle')}</p>
        </motion.div>

        {order ? (
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-6"
          >
            {/* Order Header Card */}
            <div className="glass p-6 flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div>
                <p className="label-text mb-1">{t('order.orderNumber')}</p>
                <p className="text-2xl font-bold text-charcoal" dir="ltr">{order.order_number}</p>
                <p className="text-charcoal/60 text-sm mt-1">{order.customer_name}</p>
              </div>
              <div className="flex flex-col items-start md:items-end gap-2">
                <span
                  className={`text-xs font-semibold px-3 py-1.5 ${
                    STATUS_CLASSES[order.status] || STATUS_CLASSES.active
                  }`}
                >
                  {t(`order.dashboard.status${order.status.charAt(0).toUpperCase() + order.status.slice(1)}`)}
                </span>
                <p className="text-charcoal/50 text-sm">
                  {t('order.dashboard.currentStep')} {order.current_step} / 6
                </p>
              </div>
            </div>

            {/* Timeline */}
            <div className="glass p-6 md:p-8">
              <p className="label-text mb-6">{t('order.dashboard.timeline')}</p>
              <div className="space-y-0">
                {STEP_KEYS.map((key, index) => {
                  const stepNum = index + 1;
                  const Icon = STEP_ICONS[index];
                  const isCompleted = stepNum < order.current_step;
                  const isActive = stepNum === order.current_step;
                  const isPending = stepNum > order.current_step;

                  return (
                    <div key={key} className="flex items-start gap-5">
                      <div className="flex flex-col items-center">
                        <div
                          className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                            isCompleted
                              ? 'bg-gold'
                              : isActive
                              ? 'bg-gold/10 border-2 border-gold'
                              : 'bg-charcoal/5 border border-charcoal/10'
                          }`}
                        >
                          {isCompleted ? (
                            <CheckCircle2 size={18} className="text-white" />
                          ) : (
                            <Icon size={15} className={isActive ? 'text-gold' : 'text-charcoal/25'} />
                          )}
                        </div>
                        {index < STEP_KEYS.length - 1 && (
                          <div
                            className={`w-0.5 h-12 transition-colors ${
                              isCompleted ? 'bg-gold' : 'bg-charcoal/8'
                            }`}
                          />
                        )}
                      </div>
                      <div className="flex-1 pt-2 pb-8">
                        <div className="flex items-start justify-between gap-4">
                          <div>
                            <p
                              className={`font-semibold ${
                                isActive
                                  ? 'text-charcoal'
                                  : isCompleted
                                  ? 'text-charcoal/60'
                                  : 'text-charcoal/30'
                              }`}
                            >
                              {t(`order.steps.${key}.title`)}
                            </p>
                            <p className={`text-sm mt-0.5 ${isActive ? 'text-charcoal/60' : 'text-charcoal/30'}`}>
                              {t(`order.steps.${key}.desc`)}
                            </p>
                          </div>
                          {isCompleted && (
                            <span className="text-xs text-green-600 font-medium flex-shrink-0">
                              {t('order.dashboard.completed')}
                            </span>
                          )}
                          {isActive && (
                            <span className="text-xs text-gold font-medium flex-shrink-0 flex items-center gap-1">
                              <span className="w-1.5 h-1.5 rounded-full bg-gold animate-pulse inline-block" />
                              {t('order.dashboard.inProgress')}
                            </span>
                          )}
                        </div>

                        {/* Step-specific details */}
                        {key === 'techVisit' && order.technician_visit?.date && (
                          <p className="text-xs text-charcoal/40 mt-1 flex items-center gap-1">
                            <Clock size={11} /> {order.technician_visit.date} — {order.technician_visit.time_slot}
                          </p>
                        )}
                        {key === 'showroom' && order.showroom_visit?.date && (
                          <p className="text-xs text-charcoal/40 mt-1 flex items-center gap-1">
                            <Clock size={11} /> {order.showroom_visit.date} — {order.showroom_visit.time_slot}
                          </p>
                        )}
                        {key === 'production' && order.production?.status && order.production.status !== 'pending' && (
                          <p className="text-xs text-gold mt-1">{t(`order.production.${order.production.status}`)}</p>
                        )}
                        {key === 'installation' && order.installation?.date && (
                          <p className="text-xs text-charcoal/40 mt-1 flex items-center gap-1">
                            <Clock size={11} /> {order.installation.date} — {order.installation.time_slot}
                          </p>
                        )}
                        {key === 'agreement' && order.agreement?.total_price && (
                          <p className="text-xs text-charcoal/50 mt-1">
                            {order.agreement.total_price.toLocaleString()} {t('order.currency')}
                          </p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Actions */}
            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                to={currentStepRoute}
                className="flex-1 bg-gold text-charcoal-deep font-bold py-4 flex items-center justify-center gap-3 hover:opacity-90 transition-opacity"
              >
                {t('order.dashboard.continue')} <ArrowRight size={18} />
              </Link>
              <a
                href={waLink}
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1 border-2 border-[#25D366] text-charcoal font-bold py-4 flex items-center justify-center gap-3 hover:bg-[#25D366]/5 transition-colors"
              >
                <MessageCircle size={18} className="text-[#25D366]" />
                {t('order.dashboard.contactAdmin')}
              </a>
            </div>

            {/* Admin notes */}
            {order.admin_notes && (
              <div className="bg-charcoal/4 border border-charcoal/10 p-5">
                <p className="text-sm font-medium text-charcoal/60 mb-1">{t('order.adminNotes')}</p>
                <p className="text-charcoal text-sm">{order.admin_notes}</p>
              </div>
            )}
          </motion.div>
        ) : (
          /* Lookup Form */
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            className="glass p-8 md:p-10 space-y-6"
          >
            <div className="text-center mb-2">
              <div className="w-14 h-14 bg-gold/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Search size={24} className="text-gold" />
              </div>
              <h2 className="text-xl font-bold text-charcoal">{t('order.dashboard.lookupTitle')}</h2>
              <p className="text-charcoal/55 text-sm mt-1">{t('order.dashboard.lookupDesc')}</p>
            </div>

            <form onSubmit={handleLookup} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-charcoal/70 mb-2">
                  {t('order.dashboard.orderNumLabel')}
                </label>
                <input
                  type="text"
                  value={lookupForm.orderNumber}
                  onChange={(e) => setLookupForm((p) => ({ ...p, orderNumber: e.target.value }))}
                  className={inputCls}
                  placeholder={t('order.dashboard.orderNumPlaceholder')}
                  dir="ltr"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-charcoal/70 mb-2">
                  {t('order.credentials.password')}
                </label>
                <input
                  type="text"
                  value={lookupForm.password}
                  onChange={(e) => setLookupForm((p) => ({ ...p, password: e.target.value }))}
                  className={inputCls}
                  placeholder="123456"
                  dir="ltr"
                  maxLength={6}
                  inputMode="numeric"
                />
              </div>

              {lookupError && (
                <p className="text-red-500 text-sm">{t('order.dashboard.notFound')}</p>
              )}

              <button
                type="submit"
                disabled={lookupLoading}
                className="w-full bg-gold text-charcoal-deep font-bold py-4 flex items-center justify-center gap-3 hover:opacity-90 transition-opacity disabled:opacity-50"
              >
                {lookupLoading ? (
                  <><Loader2 size={18} className="animate-spin" /> {t('order.loading')}</>
                ) : (
                  <><Search size={18} /> {t('order.dashboard.find')}</>
                )}
              </button>
            </form>

            <div className="text-center pt-2 border-t border-charcoal/8">
              <p className="text-charcoal/40 text-sm mb-3">{t('order.dashboard.noOrder')}</p>
              <Link
                to="/start"
                className="text-gold font-semibold hover:underline flex items-center gap-2 justify-center"
              >
                {t('order.dashboard.startNew')} <ArrowRight size={16} />
              </Link>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default ClientDashboardPage;
